import { useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { StarIcon } from '@phosphor-icons/react';
import { FormTextareaField } from '@/components/ui/form-textarea-field';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/hooks/useAuth';
import { feedbackFormSchema } from '@/lib/validators/feedback-form.validator';
import { createFeedback } from '@/lib/services/feedback';
import type { FeedbackFormValues } from '@/lib/validators/feedback-form.validator';
import { AccountContent } from '@/content/AccountContent';
import { Banner } from './Banner';

/**
 * Default values for the feedback form
 */
const feedbackFormDefaultValues: FeedbackFormValues = {
  rating: 0,
  message: '',
  isBug: false,
};

/**
 * Convert FeedbackFormValues to FeedbackInsert format
 */
function formValuesToFeedbackInsert(
  values: FeedbackFormValues,
  userId: string
) {
  const appVersion = import.meta.env.VITE_APP_VERSION || null;

  return {
    user_id: userId,
    rating: values.rating,
    message: values.message.trim(),
    is_bug: values.isBug,
    app_version: appVersion,
  };
}

type FeedbackFormProps = {
  onSuccess?: () => void;
};

/**
 * Star rating component
 */
function StarRating({
  value,
  onChange,
}: {
  value: number;
  onChange: (rating: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div className="flex gap-1 justify-center">
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = hovered !== null ? star <= hovered : star <= value;

        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(null)}
            className="p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-600 rounded"
            aria-label={`Rate ${star} out of 5 stars`}
          >
            <StarIcon
              size={40}
              weight={isFilled ? 'fill' : 'regular'}
              className={isFilled ? 'text-sky-300' : 'text-slate-400'}
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Feedback form component for submitting feedback and bug reports
 */
export function FeedbackForm({ onSuccess }: FeedbackFormProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  const form = useForm({
    defaultValues: feedbackFormDefaultValues,
    onSubmit: async ({ value }: { value: FeedbackFormValues }) => {
      setSubmitAttempted(true);

      // Validate form before submitting
      const validationResult = feedbackFormSchema.safeParse(value);

      if (!validationResult.success) {
        const flattened = validationResult.error.flatten();
        setValidationErrors(flattened.fieldErrors as Record<string, string[]>);
        return;
      }

      // Validation passed - clear errors and submit
      setValidationErrors({});
      setIsSubmitting(true);

      try {
        if (!user?.id) {
          throw new Error('User must be authenticated to submit feedback');
        }

        const feedbackInsert = formValuesToFeedbackInsert(value, user.id);
        await createFeedback(feedbackInsert);

        setIsSuccess(true);
        form.reset(feedbackFormDefaultValues);
        setSubmitAttempted(false);

        // Reset success state after 3 seconds
        setTimeout(() => {
          setIsSuccess(false);
          onSuccess?.();
        }, 3000);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        setValidationErrors({
          submit: [
            error instanceof Error
              ? error.message
              : AccountContent.feedback.form.submit.error,
          ],
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const ratingErrors = validationErrors.rating || [];
  const messageErrors = validationErrors.message || [];
  const submitErrors = validationErrors.submit || [];

  return (
    <div className="bg-slate-600 rounded-xl p-6">
      <p className="text-body text-slate-300 mb-6">
        {
          "Any feature requests or issues you have with the app? We'd love to hear from you!"
        }
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-6"
      >
        {/* Star Rating */}
        <form.Field name="rating">
          {(field) => (
            <div className="space-y-2 flex flex-col items-center">
              <Label className="sr-only">
                {AccountContent.feedback.form.rating.label}
              </Label>
              <StarRating
                value={field.state.value}
                onChange={(rating) => field.handleChange(rating)}
              />
              {submitAttempted && ratingErrors.length > 0 && (
                <p className="text-sm text-red-400">{ratingErrors[0]}</p>
              )}
            </div>
          )}
        </form.Field>

        {/* Message */}
        <form.Field name="message">
          {(field) => (
            <FormTextareaField
              field={field}
              label={AccountContent.feedback.form.message.label}
              placeholder={AccountContent.feedback.form.message.placeholder}
              helperText={AccountContent.feedback.form.message.helperText}
              maxLength={2000}
              forceShowErrors={submitAttempted}
              externalErrors={messageErrors}
            />
          )}
        </form.Field>

        {/* Bug Checkbox */}
        <form.Field name="isBug">
          {(field) => (
            <div className="flex items-center gap-3">
              <Checkbox
                id="isBug"
                checked={field.state.value}
                onCheckedChange={(checked) =>
                  field.handleChange(checked === true)
                }
                className="h-5 w-5 border-slate-400 data-[state=checked]:bg-purple-400 data-[state=checked]:border-purple-400"
              />
              <Label
                htmlFor="isBug"
                className="text-slate-200 cursor-pointer select-none"
              >
                {AccountContent.feedback.form.isBug.label}
              </Label>
            </div>
          )}
        </form.Field>

        {/* Submit Error */}
        {submitErrors.length > 0 && (
          <Banner variant="error" description={submitErrors[0]} />
        )}

        {/* Success Message */}
        {isSuccess && (
          <Banner
            variant="success"
            description={AccountContent.feedback.form.submit.success}
          />
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || isSuccess}
          className="w-full"
          size="lg"
        >
          {isSubmitting
            ? AccountContent.feedback.form.submit.submitting
            : AccountContent.feedback.form.submit.label}
        </Button>
      </form>
    </div>
  );
}
